import React from 'react';

/**
 * Reusable Pagination Component for TanStack Tables
 * 
 * @param {number} currentPage - Current active page (1-indexed)
 * @param {number} totalPages - Total number of pages
 * @param {number} totalCount - Total number of records
 * @param {number} pageSize - Number of items per page
 * @param {Array} data - Current page data array
 * @param {Function} onPageChange - Callback when page changes
 * @param {Function} onPageSizeChange - Callback when page size changes
 * @param {Array} pageSizeOptions - Available page size options (default: [10, 25, 30, 50, 100])
 * @param {number} maxPageButtons - Maximum page buttons to show (default: 5)
 */
const TablePagination = ({
  currentPage = 1,
  totalPages = 1,
  totalCount = 0,
  pageSize = 10,
  data = [],
  onPageChange,
  onPageSizeChange,
  pageSizeOptions = [10, 25, 30, 50, 100],
  maxPageButtons = 5
}) => {
  
  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages && newPage !== currentPage) {
      onPageChange(newPage);
    }
  };

  const handleSizeChange = (e) => {
    const newSize = parseInt(e.target.value);
    if (onPageSizeChange) {
      onPageSizeChange(newSize);
    }
    e.target.blur();
  };

  // Calculate page range to display
  const getPageRange = () => {
    const range = [];
    let startPage = Math.max(1, currentPage - Math.floor(maxPageButtons / 2));
    let endPage = Math.min(totalPages, startPage + maxPageButtons - 1);

    // Adjust start if we're near the end
    if (endPage - startPage < maxPageButtons - 1) {
      startPage = Math.max(1, endPage - maxPageButtons + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      range.push(i);
    }

    return range;
  };

  const pageRange = getPageRange();
  const startRecord = data.length > 0 ? ((currentPage - 1) * pageSize + 1) : 0;
  const endRecord = Math.min(currentPage * pageSize, totalCount);

  return (
    <div className="d-flex justify-content-between align-items-center mt-3">
  <div className="row w-100">
    
    {/* Page Size Selector - Col 6 */}
    <div className="col-6">
      <div className="d-flex align-items-center gap-2">
        <style>{`
          .custom-select {
            background-color: #74788d;
            border-color: #74788d;
            color: #f5f6f8;
            width: auto;
            background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16'%3E%3Cpath fill='%23f5f6f8' d='M7.247 11.14 2.451 5.658C1.885 5.013 2.345 4 3.204 4h9.592a1 1 0 0 1 .753 1.659l-4.796 5.48a1 1 0 0 1-1.506 0z'/%3E%3C/svg%3E");
          }
          .custom-select:hover,
          .custom-select:focus {
            color: #fff;
            background-color: #636678;
            border-color: #5d6071;
            box-shadow: none;
          }
        `}</style>
        <select 
          className="form-select form-select-sm custom-select"
          value={pageSize}
          onChange={handleSizeChange}
        >
          {pageSizeOptions.map(size => (
            <option key={size} value={size}>{size}</option>
          ))}
        </select>
      </div>
    </div>

    {/* Pagination Buttons - Col 6 */}
    <div className="col-6">
      <div className="d-flex justify-content-between align-items-center">
        {totalPages > 0 && (
          <nav>
            <ul className="pagination mb-0">
              {/* First Page */}
              <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                <button 
                  className="page-link" 
                  onClick={() => handlePageChange(1)}
                  disabled={currentPage === 1}
                  aria-label="First page"
                >
                  &lt;&lt;
                </button>
              </li>
              
              {/* Previous Page */}
              <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                <button 
                  className="page-link" 
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  aria-label="Previous page"
                >
                  &lt;
                </button>
              </li>
              
              {/* Page Numbers */}
              {pageRange.map(pageNum => (
                <li 
                  key={pageNum} 
                  className={`page-item ${currentPage === pageNum ? 'active' : ''}`}
                >
                  <button 
                    className="page-link" 
                    onClick={() => handlePageChange(pageNum)}
                  >
                    {pageNum}
                  </button>
                </li>
              ))}
              
              {/* Next Page */}
              <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                <button 
                  className="page-link" 
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  aria-label="Next page"
                >
                  &gt;
                </button>
              </li>
              
              {/* Last Page */}
              <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                <button 
                  className="page-link" 
                  onClick={() => handlePageChange(totalPages)}
                  disabled={currentPage === totalPages}
                  aria-label="Last page"
                >
                  &gt;&gt;
                </button>
              </li>
            </ul>
          </nav>
        )}
      </div>
    </div>

  </div>
</div>
  );
};

export default TablePagination;
